import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { SETTINGS_BY_SLUG, SETTINGS_GROUPS, canOpenSetting } from '../components/settings/settingsCatalog';
import { BackIcon, ChevronIcon } from '../components/settings/SettingsIcons';
import '../styles/OrgAdminSettings.css';

// One settings screen, full width.
//
// The panels rendered here are the SAME components the old stacked page used —
// each was already a self-contained Card, so this route is a container and
// nothing more. Rewriting them for a new shell would have meant six components
// with two layouts each.
//
// An unknown slug redirects to the index rather than 404ing: a stale bookmark
// from before a setting was renamed should land somewhere useful.

function OrgAdminSettingsDetail() {
  const { section } = useParams();
  const { user } = useAuth();
  const entry = SETTINGS_BY_SLUG[section];

  // Unknown slug, or one this user may not open, both go back to the index —
  // the same rule the menu applies, so a typed URL can't get further than a tap.
  if (!entry || !canOpenSetting(user, entry)) {
    return <Navigate to="/org-admin/settings" replace />;
  }

  const { Component, label, description, group } = entry;

  // Sibling screens, for the footer pager. Jumping between Auditors and
  // Checklist Templates is common enough that going back to the index each
  // time is a tax.
  const siblings = group.items.filter(
    (i) => i.slug !== section && canOpenSetting(user, i)
  );

  return (
    <OrgAdminLayout title={label} subtitle={description}>
      <div className="set-detail">
        <nav className="set-crumb" aria-label="Breadcrumb">
          <Link to="/org-admin/settings" className="set-back">
            <BackIcon />
            <span>Settings</span>
          </Link>
          <span className="set-crumb-sep" aria-hidden="true"><ChevronIcon /></span>
          <span className="set-crumb-current">{group.title}</span>
        </nav>

        <div className="set-detail-body">
          <Component />
        </div>

        {siblings.length > 0 && (
          <div className="set-more">
            <span className="set-more-title">More in {group.title}</span>
            <div className="set-list">
              {siblings.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.slug} to={`/org-admin/settings/${item.slug}`} className="set-row">
                    <span className="set-row-icon"><Icon /></span>
                    <span className="set-row-text">
                      <span className="set-row-label">{item.label}</span>
                      <span className="set-row-desc">{item.description}</span>
                    </span>
                    <span className="set-row-chevron"><ChevronIcon /></span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </OrgAdminLayout>
  );
}

// Re-exported so the route file doesn't need to know about the catalog.
export { SETTINGS_GROUPS };
export default OrgAdminSettingsDetail;
